// Cross-sell Tracking SDK - Main Tracker Class

import type {
  ContactType,
  CrossSellConfig,
  CrossSellEnvironment,
  CrossSellError,
  GetContactRedirectUrlParams,
  GetRedirectUrlParams,
  InitAppRequest,
  InitAppResponse,
  LogEventRequest,
  LogEventResponse,
  SourceType,
} from "./types"
import { CONTACT_TYPES, CROSS_SELL_APP_KEY } from "./types"

/**
 * Cross-sell Tracking SDK 主类
 */
export class CrossSellTracker {
  private readonly apiKey: CROSS_SELL_APP_KEY
  private readonly baseUrl: string
  private readonly enableLogging: boolean
  private readonly environment: "test" | "production"
  private traceId: string | null = null

  private readonly environments: CrossSellEnvironment = {
    test: "cross-sell-test.parcelpanel.com",
    production: "cross-sell.parcelwill.net",
  }

  // 统一跳转URL配置
  private readonly redirectUrls = {
    test: "https://cross-sell-test.parcelpanel.com/v1/install-redirect",
    production: "https://cross-sell.parcelwill.net/v1/install-redirect",
  }

  // Contact Cross Sell 跳转URL配置
  private readonly contactRedirectUrls = {
    test: "https://cross-sell-test.parcelpanel.com/v1/contact-redirect",
    production: "https://cross-sell.parcelwill.net/v1/contact-redirect",
  }

  constructor(config: CrossSellConfig) {
    this.apiKey = config.apiKey
    this.environment = config.environment || "production"
    this.baseUrl = `https://${this.environments[this.environment]}`
    this.enableLogging = config.enableLogging || false
  }

  /**
   * 获取当前trace ID
   */
  public getTraceId(): string | null {
    return this.traceId
  }

  /**
   * 设置trace ID（用于手动设置）
   */
  public setTraceId(traceId: string): void {
    this.traceId = traceId
  }

  /**
   * 获取当前环境
   */
  public getEnvironment(): "test" | "production" {
    return this.environment
  }

  /**
   * 获取跳转地址
   */
  public getRedirectUrl(params: GetRedirectUrlParams, targetEnvironment?: "test" | "production"): string | null {
    // 优先使用外部提供的baseUrl，否则使用统一的跳转地址
    let { baseUrl } = params

    // 如果没有提供baseUrl，则使用统一配置
    if (!baseUrl) {
      const environment = targetEnvironment || this.environment

      baseUrl = this.redirectUrls[environment]
    }

    // 确定referrer_app参数
    let referrerApp = "parcelpanel"

    if (this.apiKey) {
      referrerApp = this.apiKey === CROSS_SELL_APP_KEY.PARCEL_PANEL ? "parcelpanel" : "return"
    }

    // 构建查询参数
    const queryParams = new URLSearchParams({
      referrer_app: referrerApp,
      target_app: params.target_app,
      location_uuid: params.location_uuid,
      redirect_url: params.redirect_url,
    })

    // 添加trace_id（如果提供或存在）
    const traceId = params.trace_id || this.traceId

    if (traceId) {
      queryParams.append("trace_id", traceId)
    }

    const redirectUrl = `${baseUrl}?${queryParams.toString()}`

    this.log("Generated redirect URL", {
      apiKey: this.apiKey,
      referrerApp,
      targetApp: params.target_app,
      customBaseUrl: !!params.baseUrl,
      baseUrl,
      redirectUrl,
    })

    return redirectUrl
  }

  /**
   * APP初始化
   */
  public async initApp(params: InitAppRequest): Promise<InitAppResponse | null> {
    try {
      const url = `${this.baseUrl}/v1/app/init`
      const response = await this.makeRequest<InitAppResponse>(url, {
        method: "POST",
        headers: this.buildHeaders(),
        body: JSON.stringify(params),
      })

      if (response?.data?.trace_id) {
        this.traceId = response.data.trace_id
        this.log("APP initialized successfully", {
          traceId: this.traceId,
          domain: params.domain,
        })
      }

      return response
    } catch (error) {
      this.handleError("Failed to initialize app", error)
      return null
    }
  }

  /**
   * 事件上报
   */
  public async logEvent(params: LogEventRequest): Promise<LogEventResponse | null> {
    try {
      const url = `${this.baseUrl}/v1/event/log`
      const response = await this.makeRequest<LogEventResponse>(url, {
        method: "POST",
        headers: this.buildHeaders(true),
        body: JSON.stringify(params),
      })

      this.log("Event logged successfully", { event: params.event, locationUuid: params.location_uuid })
      return response
    } catch (error) {
      this.handleError("Failed to log event", error, { event: params.event })
      return null
    }
  }

  /**
   * 快捷方法：上报浏览事件
   */
  public async logViewed(locationUuid: string): Promise<LogEventResponse | null> {
    return this.logEvent({
      location_uuid: locationUuid,
      event: "viewed",
    })
  }

  /**
   * 快捷方法：上报点击事件
   */
  public async logClicked(locationUuid: string): Promise<LogEventResponse | null> {
    return this.logEvent({
      location_uuid: locationUuid,
      event: "clicked",
    })
  }

  /**
   * 快捷方法：上报安装事件
   */
  public async logInstalled(sourceTraceId?: string): Promise<LogEventResponse | null> {
    return this.logEvent({
      event: "installed",
      source_trace_id: sourceTraceId,
    })
  }

  /**
   * 快捷方法：上报订阅事件
   */
  public async logSubscribed(planName: string, planPrice: string | number): Promise<LogEventResponse | null> {
    return this.logEvent({
      event: "subscribed",
      plan_name: planName,
      plan_price: planPrice,
    })
  }

  /**
   * 快捷方法：上报发布事件（针对Insurance）
   */
  public async logPublished(): Promise<LogEventResponse | null> {
    return this.logEvent({
      event: "published",
    })
  }

  /**
   * 快捷方法：跳转到Return安装页面
   */
  public redirectToReturnInstall(locationUuid: string, queryParams?: Record<string, string> | string): string | null {
    // 构建Return安装页面的完整URL
    let redirectUrl = "https://apps.shopify.com/parcel-panel-returns-exchanges"

    if (queryParams) {
      let urlSearchParams: URLSearchParams

      if (typeof queryParams === "string") {
        // 处理字符串格式：先解析再重新编码，确保正确的URL编码
        const cleanString = queryParams.startsWith("?") ? queryParams.substring(1) : queryParams

        try {
          // 使用URLSearchParams解析字符串，自动处理编码
          urlSearchParams = new URLSearchParams(cleanString)
        } catch (error) {
          this.log("Invalid query string format", { queryParams, error }, "warn")
          // 如果解析失败，创建空的URLSearchParams
          urlSearchParams = new URLSearchParams()
        }
      } else {
        // 对象格式：直接创建URLSearchParams，自动处理编码
        urlSearchParams = new URLSearchParams(queryParams)
      }

      const queryString = urlSearchParams.toString()

      if (queryString) {
        redirectUrl += `?${queryString}`
      }
    }

    // 生成跳转链接
    const jumpUrl = this.getRedirectUrl({
      target_app: "return",
      location_uuid: locationUuid,
      redirect_url: redirectUrl,
    })

    // 自动在新窗口打开
    if (jumpUrl && typeof window !== "undefined") {
      window.open(jumpUrl, "_blank")
    }

    this.log("Redirected to Return install page", { location_uuid: locationUuid, queryParams, jumpUrl })

    return jumpUrl
  }

  /**
   * 快捷方法：跳转到ParcelWILL安装页面
   */
  public redirectToParcelPanelInstall(locationUuid: string, queryParams?: Record<string, string> | string): string | null {
    // 构建ParcelPanel安装页面的完整URL
    let redirectUrl = "https://apps.shopify.com/parcelpanel"

    if (queryParams) {
      let urlSearchParams: URLSearchParams

      if (typeof queryParams === "string") {
        // 处理字符串格式：先解析再重新编码，确保正确的URL编码
        const cleanString = queryParams.startsWith("?") ? queryParams.substring(1) : queryParams

        try {
          // 使用URLSearchParams解析字符串，自动处理编码
          urlSearchParams = new URLSearchParams(cleanString)
        } catch (error) {
          this.log("Invalid query string format", { queryParams, error }, "warn")
          // 如果解析失败，创建空的URLSearchParams
          urlSearchParams = new URLSearchParams()
        }
      } else {
        // 对象格式：直接创建URLSearchParams，自动处理编码
        urlSearchParams = new URLSearchParams(queryParams)
      }

      const queryString = urlSearchParams.toString()

      if (queryString) {
        redirectUrl += `?${queryString}`
      }
    }

    // 生成跳转链接
    const jumpUrl = this.getRedirectUrl({
      target_app: "parcelpanel",
      location_uuid: locationUuid,
      redirect_url: redirectUrl,
    })

    // 自动在新窗口打开
    if (jumpUrl && typeof window !== "undefined") {
      window.open(jumpUrl, "_blank")
    }

    this.log("Redirected to ParcelWILL install page", { location_uuid: locationUuid, queryParams, jumpUrl })

    return jumpUrl
  }

  /**
   * 获取 Contact Cross Sell 跳转地址
   */
  public getContactRedirectUrl(params: GetContactRedirectUrlParams, targetEnvironment?: "test" | "production"): string | null {
    const environment = targetEnvironment || this.environment
    const baseUrl = this.contactRedirectUrls[environment]

    // 确定referrer_app参数
    let referrerApp = "parcelpanel"

    if (this.apiKey) {
      referrerApp = this.apiKey === CROSS_SELL_APP_KEY.PARCEL_PANEL ? "parcelpanel" : "return"
    }

    // 构建查询参数
    const queryParams = new URLSearchParams({
      referrer_app: referrerApp,
      location_uuid: params.location_uuid,
      source: params.source || "app",
      contact_type: params.contact_type,
      redirect_url: params.redirect_url,
    })

    // 添加trace_id（如果提供或存在）
    const traceId = params.trace_id || this.traceId

    if (traceId) {
      queryParams.append("trace_id", traceId)
    }

    const contactRedirectUrl = `${baseUrl}?${queryParams.toString()}`

    this.log("Generated contact redirect URL", {
      apiKey: this.apiKey,
      referrerApp,
      environment,
      contactType: params.contact_type,
      source: params.source,
      contactRedirectUrl,
    })

    return contactRedirectUrl
  }

  /**
   * 快捷方法：跳转到 WhatsApp 联系
   */
  public redirectToWhatsApp(locationUuid: string, redirectUrl: string, source?: SourceType): string | null {
    const jumpUrl = this.getContactRedirectUrl({
      location_uuid: locationUuid,
      contact_type: CONTACT_TYPES.WHATS_APP,
      source,
      redirect_url: redirectUrl,
    })

    // 自动在新窗口打开
    if (jumpUrl && typeof window !== "undefined") {
      window.open(jumpUrl, "_blank")
    }

    this.log("Redirected to WhatsApp contact", { location_uuid: locationUuid, source, redirectUrl, jumpUrl })

    return jumpUrl
  }

  /**
   * 快捷方法：跳转到 Slack 联系
   */
  public redirectToSlack(locationUuid: string, redirectUrl: string, source?: SourceType): string | null {
    const jumpUrl = this.getContactRedirectUrl({
      location_uuid: locationUuid,
      contact_type: CONTACT_TYPES.SLACK,
      source,
      redirect_url: redirectUrl,
    })

    // 自动在新窗口打开
    if (jumpUrl && typeof window !== "undefined") {
      window.open(jumpUrl, "_blank")
    }

    this.log("Redirected to Slack contact", { location_uuid: locationUuid, source, redirectUrl, jumpUrl })

    return jumpUrl
  }

  /**
   * 快捷方法：跳转到企业微信联系
   */
  public redirectToWeCom(locationUuid: string, redirectUrl: string, source?: SourceType): string | null {
    const jumpUrl = this.getContactRedirectUrl({
      location_uuid: locationUuid,
      contact_type: CONTACT_TYPES.WE_COM,
      source,
      redirect_url: redirectUrl,
    })

    // 自动在新窗口打开
    if (jumpUrl && typeof window !== "undefined") {
      window.open(jumpUrl, "_blank")
    }

    this.log("Redirected to WeCom contact", { location_uuid: locationUuid, source, redirectUrl, jumpUrl })

    return jumpUrl
  }

  /**
   * 快捷方法：跳转到 Demo 联系
   */
  public redirectToDemo(locationUuid: string, redirectUrl: string, source?: SourceType): string | null {
    const jumpUrl = this.getContactRedirectUrl({
      location_uuid: locationUuid,
      contact_type: CONTACT_TYPES.DEMO,
      source,
      redirect_url: redirectUrl,
    })

    // 自动在新窗口打开
    if (jumpUrl && typeof window !== "undefined") {
      window.open(jumpUrl, "_blank")
    }

    this.log("Redirected to Demo contact", { location_uuid: locationUuid, source, redirectUrl, jumpUrl })

    return jumpUrl
  }

  /**
   * 通用的 Contact Cross Sell 跳转方法
   */
  public redirectToContact(locationUuid: string, contactType: ContactType, redirectUrl: string, source?: SourceType): string | null {
    const jumpUrl = this.getContactRedirectUrl({
      location_uuid: locationUuid,
      contact_type: contactType,
      source,
      redirect_url: redirectUrl,
    })

    // 自动在新窗口打开
    if (jumpUrl && typeof window !== "undefined") {
      window.open(jumpUrl, "_blank")
    }

    this.log("Redirected to contact", { location_uuid: locationUuid, contact_type: contactType, source, redirectUrl, jumpUrl })

    return jumpUrl
  }

  /**
   * 构建请求头
   */
  private buildHeaders(includeTraceId: boolean = false): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Api-Key": this.apiKey,
    }

    if (includeTraceId && this.traceId) {
      headers["X-Trace-Id"] = this.traceId
    }

    return headers
  }

  /**
   * 发起HTTP请求
   */
  private async makeRequest<T>(url: string, options: RequestInit): Promise<T | null> {
    try {
      const response = await fetch(url, options)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      return data as T
    } catch (error) {
      throw error
    }
  }

  /**
   * 错误处理
   */
  private handleError(message: string, error: any, context?: Record<string, any>): void {
    const errorInfo: CrossSellError = {
      message,
      originalError: error,
      code: error?.status || error?.code,
    }

    this.log(message, { error: errorInfo, context }, "error")

    // 根据需求文档要求，不影响业务，捕获并丢弃错误
    // 这里可以选择上报到监控系统，但不抛出异常
  }

  /**
   * 日志输出
   */
  private log(message: string, data?: any, level: "info" | "error" | "warn" = "info"): void {
    if (!this.enableLogging) return

    const logData = {
      timestamp: new Date().toISOString(),
      level,
      message,
      traceId: this.traceId,
      ...data,
    }

    if (level === "error") {
      console.error("[CrossSellTracker]", logData)
    } else if (level === "warn") {
      console.warn("[CrossSellTracker]", logData)
    } else {
      // console.log("[CrossSellTracker]", logData)
    }
  }
}
